import { useState } from 'react';
import { useAuth } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import CredentialsManager from '../components/client/CredentialsManager';
import ImportModal from '../components/ImportModal';
import { Button } from '../../design-system';

// The org-wide credential vault cockpit. Reuses the same CredentialsManager that
// powers the per-client card — with no clientId it lists every client's logins,
// filterable, with add/reveal/login/delete. When RBAC lands, this whole route is
// the single place to gate with `credentials.view`.

// ⚠️ Import is ADMIN-ONLY (level 5), a higher bar than the rest of this page.
// Adding one login is routine; loading a firm's entire set of client passwords
// from one spreadsheet is not. The server enforces it too (credentialRoutes) —
// this only decides whether the button is offered, and a hidden button is not a
// security control.
const ADMIN_LEVEL = 5;

function OrgAdminCredentials() {
  const { user } = useAuth();
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const canImport = !!user?.isSuperAdmin || (user?.role?.level || 0) >= ADMIN_LEVEL;

  return (
    <OrgAdminLayout
      title="Credentials"
      subtitle="Clients' government-portal logins"
      headerRight={
        canImport ? (
          <Button variant="secondary" size="sm" onClick={() => setImportOpen(true)}>Import</Button>
        ) : null
      }
    >
      {/* Remounted after an import so the list reflects what was just added —
          CredentialsManager fetches on mount and owns its own state. */}
      <CredentialsManager key={reloadKey} />

      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => setReloadKey((k) => k + 1)}
        basePath="/api/credentials"
        title="Import portal logins from a spreadsheet"
        noun="login"
        nounPlural="logins"
        templateFile="credential-import-template.xlsx"
        warning={
          'The file you fill in will contain PLAINTEXT PASSWORDS. They are encrypted the instant '
          + 'the server reads them, but the spreadsheet on your own machine is not — delete it '
          + 'securely once the import has run, and never email it or leave it in a shared drive.'
        }
        describeTemplate="An Excel file with the columns we accept, plus sheets listing your clients and the portals you can store logins for. One row = one login."
        describeUpload="This only ADDS logins — it never overwrites an existing one, so a stale spreadsheet cannot silently replace a working login. There is no export of this register, by design."
        rowLabel={(r) => r.label}
      />
    </OrgAdminLayout>
  );
}

export default OrgAdminCredentials;
